import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import PageContainer from "../../../components/PageContainer";
import {
  abrirCaja,
  cajaActual,
  cerrarCaja,
  movimientosCaja,
  reporteCajaSesion,
  registrarMovimientoManual,
} from "../../../services/modules/Caja";
import { getCajas } from "../../../services/modules/Cajas";
import { useCaja } from "../../../context/CajaContext";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

const formatMoney = (value) => {
  const num = Number(value || 0);
  return new Intl.NumberFormat("es-GT", { style: "currency", currency: "GTQ" }).format(num);
};

const STORAGE_KEY_CAJA_ID = "selected_caja_id";
const getStoredCajaId = () => {
  const raw = localStorage.getItem(STORAGE_KEY_CAJA_ID);
  const id = Number(raw);
  return Number.isFinite(id) && id > 0 ? id : null;
};

const round2 = (value) => +Number(value || 0).toFixed(2);

const generarPDFCierre = (cierreReporte) => {
  if (!cierreReporte) return;

  const sesion = cierreReporte?.sesion || {};
  const cajaNombre = cierreReporte?.sesion?.caja_nombre || cierreReporte?.caja?.nombre || `Caja #${sesion?.id_caja || cierreReporte?.id_caja || ""}`;
  const tot = cierreReporte?.totales_caja || cierreReporte?.totales || {};
  const desglose = cierreReporte?.desglose || {};
  const movs = Array.isArray(cierreReporte?.movimientos_enriquecidos)
    ? cierreReporte.movimientos_enriquecidos
    : Array.isArray(cierreReporte?.movimientos)
      ? cierreReporte.movimientos
      : [];

  const montoSistema = cierreReporte?.result?.monto_sistema ?? sesion?.monto_cierre_sistema ?? tot?.sistema ?? 0;
  const diferencia = cierreReporte?.result?.diferencia ?? sesion?.diferencia ?? 0;
  const contado = cierreReporte?.monto_cierre_reportado ?? sesion?.monto_cierre_reportado ?? 0;

  const doc = new jsPDF({ orientation: "landscape", unit: "pt", format: "A4" });
  doc.setFontSize(14);
  doc.text("Cierre de caja", 40, 40);
  doc.setFontSize(10);
  doc.text(`Caja: ${cajaNombre}`, 40, 60);
  doc.text(`Sesión: ${sesion?.id_caja_sesion || ""}`, 40, 74);
  doc.text(`Apertura: ${String(sesion?.fecha_apertura || "")}`, 40, 88);
  doc.text(`Cierre: ${String(sesion?.fecha_cierre || "")}`, 40, 102);

  const resumenHead = [["Inicial", "Ingresos", "Egresos", "Sistema", "Contado", "Diferencia"]];
  const resumenBody = [[
    formatMoney(tot?.inicial ?? 0),
    formatMoney(tot?.ingresos ?? 0),
    formatMoney(tot?.egresos ?? 0),
    formatMoney(montoSistema),
    formatMoney(contado),
    formatMoney(diferencia),
  ]];

  autoTable(doc, {
    head: resumenHead,
    body: resumenBody,
    startY: 120,
    styles: { fontSize: 9, cellPadding: 4 },
    headStyles: { fillColor: [17, 24, 39] },
    theme: "grid",
  });

  if (cierreReporte.resumen_rafaga) {
    const r = cierreReporte.resumen_rafaga;
    autoTable(doc, {
      head: [['Ráfaga cobrada (todos los métodos)', 'Reserva boletas', 'Ganancia', 'Boletas negocio', 'Boletas cliente']],
      body: [[formatMoney(r.total), formatMoney(r.costo_boletas), formatMoney(r.ganancia), r.del_negocio, r.del_cliente]],
      startY: doc.lastAutoTable.finalY + 14, styles: { fontSize: 9 }, theme: 'grid',
    });
  }
  const desgloseRows = Object.entries(desglose).map(([k, v]) => ([
    k,
    formatMoney(v?.total ?? 0),
    formatMoney(v?.costo_ext ?? 0),
    formatMoney(v?.costo ?? 0),
    formatMoney(v?.ganancia ?? 0),
  ]));

  autoTable(doc, {
    head: [["Grupo", "Total", "Costo ext", "Costo", "Ganancia"]],
    body: desgloseRows.length ? desgloseRows : [["(sin datos)", "", "", "", ""]],
    startY: doc.lastAutoTable.finalY + 14,
    styles: { fontSize: 9, cellPadding: 4 },
    headStyles: { fillColor: [17, 24, 39] },
    theme: "grid",
  });

  const movRows = movs
    .map((m) => ({
      grupo: m.grupo || "",
      fecha: String(m.fecha || ""),
      tipo: String(m.tipo || ""),
      categoria: String(m.categoria || ""),
      metodo: String(m.metodo_pago || ""),
      monto: round2(m.monto),
      costo_ext: round2(m.costo_ext ?? 0),
      costo: round2(m.costo ?? 0),
      ganancia: m.ganancia == null ? "" : round2(m.ganancia),
      descripcion: String(m.descripcion || ""),
    }))
    .sort((a, b) => {
      if (a.grupo !== b.grupo) return a.grupo.localeCompare(b.grupo);
      return a.fecha.localeCompare(b.fecha);
    })
    .map((m) => ([
      m.grupo,
      m.fecha,
      m.tipo,
      m.categoria,
      m.metodo,
      formatMoney(m.monto),
      m.costo_ext ? formatMoney(m.costo_ext) : "",
      m.costo ? formatMoney(m.costo) : "",
      m.ganancia !== "" ? formatMoney(m.ganancia) : "",
      m.descripcion,
    ]));

  autoTable(doc, {
    head: [["Grupo", "Fecha", "Tipo", "Categoría", "Método", "Monto", "Costo ext", "Costo", "Ganancia", "Descripción"]],
    body: movRows.length ? movRows : [["(sin movimientos)", "", "", "", "", "", "", "", "", ""]],
    startY: doc.lastAutoTable.finalY + 14,
    styles: { fontSize: 8, cellPadding: 3 },
    headStyles: { fillColor: [17, 24, 39] },
    theme: "grid",
  });

  const filename = `cierre_caja_${sesion?.id_caja_sesion || "sesion"}.pdf`;
  doc.save(filename);
};

const Caja = () => {
  const { refreshCaja } = useCaja();
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState(null);
  const [sesion, setSesion] = useState(null);
  const [movimientos, setMovimientos] = useState([]);
  const [cajas, setCajas] = useState([]);
  const [idCaja, setIdCaja] = useState(() => getStoredCajaId() || 1);
  const [categoriaFiltro, setCategoriaFiltro] = useState("ALL");
  const [cierreModalOpen, setCierreModalOpen] = useState(false);
  const [cierreReporte, setCierreReporte] = useState(null);

  const [montoInicial, setMontoInicial] = useState("");
  const [montoCierre, setMontoCierre] = useState("");

  const [movTipo, setMovTipo] = useState("INGRESO");
  const [movCategoria, setMovCategoria] = useState("OTROS");
  const [movMetodoPago, setMovMetodoPago] = useState("EFECTIVO");
  const [movMonto, setMovMonto] = useState("");
  const [movDescripcion, setMovDescripcion] = useState("");
  const [movProcessing, setMovProcessing] = useState(false);

  const fetchCajas = async () => {
    const data = await getCajas();
    const items = Array.isArray(data) ? data : [];
    setCajas(items);
    return items;
  };

  const fetchCaja = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await cajaActual({ id_caja: idCaja });
      setSesion(data?.sesion || null);
      if (data?.sesion?.id_caja_sesion) {
        const movs = await movimientosCaja({ id_caja_sesion: data.sesion.id_caja_sesion });
        setMovimientos(Array.isArray(movs) ? movs : []);
      } else {
        setMovimientos([]);
      }
    } catch (e) {
      setError(e?.message || "Error al cargar caja.");
      setSesion(null);
      setMovimientos([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    (async () => {
      try {
        const items = await fetchCajas();
        const validIds = new Set(items.map((c) => Number(c.id_caja)));
        const stored = getStoredCajaId();
        if (stored && validIds.has(stored)) {
          setIdCaja(stored);
          return;
        }
        const first = items?.[0]?.id_caja ? Number(items[0].id_caja) : 1;
        setIdCaja(first);
      } catch {
        // si falla listar cajas, igual deja idCaja actual
      }
    })();
  }, []);

  useEffect(() => {
    fetchCaja();
    localStorage.setItem(STORAGE_KEY_CAJA_ID, String(idCaja));
  }, [idCaja]);

  const totales = useMemo(() => {
    const ingresos = movimientos.reduce(
      (acc, m) => acc + (m.tipo === "INGRESO" ? Number(m.monto || 0) : 0),
      0
    );
    const egresos = movimientos.reduce(
      (acc, m) => acc + (m.tipo === "EGRESO" ? Number(m.monto || 0) : 0),
      0
    );
    const inicial = Number(sesion?.monto_inicial || 0);
    const sistema = +(inicial + ingresos - egresos).toFixed(2);
    return { ingresos, egresos, inicial, sistema };
  }, [movimientos, sesion?.monto_inicial]);

  const categoriasDisponibles = useMemo(() => {
    const set = new Set((movimientos || []).map((m) => m.categoria).filter(Boolean));
    return Array.from(set).sort();
  }, [movimientos]);

  const movimientosFiltrados = useMemo(() => {
    if (categoriaFiltro === "ALL") return movimientos;
    return (movimientos || []).filter((m) => m.categoria === categoriaFiltro);
  }, [movimientos, categoriaFiltro]);

  const totalesFiltrados = useMemo(() => {
    const ingresos = movimientosFiltrados.reduce(
      (acc, m) => acc + (m.tipo === "INGRESO" ? Number(m.monto || 0) : 0),
      0
    );
    const egresos = movimientosFiltrados.reduce(
      (acc, m) => acc + (m.tipo === "EGRESO" ? Number(m.monto || 0) : 0),
      0
    );
    return { ingresos: +ingresos.toFixed(2), egresos: +egresos.toFixed(2) };
  }, [movimientosFiltrados]);

  const handleAbrir = async () => {
    setProcessing(true);
    setError(null);
    try {
      const monto = Number(montoInicial || 0);
      await abrirCaja({ id_caja: idCaja, monto_inicial: monto });
      setMontoInicial("");
      await fetchCaja();
      await refreshCaja();
    } catch (e) {
      setError(e?.response?.data?.message || e?.message || "Error al abrir caja.");
    } finally {
      setProcessing(false);
    }
  };

  const handleCerrar = async () => {
    setProcessing(true);
    setError(null);
    try {
      const monto = Number(montoCierre);
      if (!Number.isFinite(monto)) {
        setError("Ingresa el monto contado para cerrar caja.");
        return;
      }
      const result = await cerrarCaja({ id_caja: idCaja, monto_cierre_reportado: monto });
      let reporte = null;
      try {
        reporte = sesion?.id_caja_sesion
          ? await reporteCajaSesion({ id_caja_sesion: sesion.id_caja_sesion })
          : null;
      } catch {
        reporte = null;
      }

      setCierreReporte({
        ...(reporte || {
          caja: cajaSeleccionada,
          id_caja: idCaja,
          sesion,
          movimientos: Array.isArray(movimientos) ? movimientos : [],
          totales,
        }),
        caja: cajaSeleccionada,
        id_caja: idCaja,
        monto_cierre_reportado: monto,
        result,
      });
      setCierreModalOpen(true);
      setMontoCierre("");
      await fetchCaja();
      await refreshCaja();
    } catch (e) {
      setError(e?.response?.data?.message || e?.message || "Error al cerrar caja.");
    } finally {
      setProcessing(false);
    }
  };

  const handleRegistrarMovimiento = async () => {
    setMovProcessing(true);
    setError(null);
    try {
      const monto = Number(movMonto);
      if (!Number.isFinite(monto) || monto <= 0) {
        setError("Ingresa un monto válido (> 0).");
        return;
      }

      await registrarMovimientoManual({
        id_caja: idCaja,
        tipo: movTipo,
        categoria: movCategoria,
        metodo_pago: movMetodoPago,
        monto,
        descripcion: movDescripcion || null,
      });

      setMovMonto("");
      setMovDescripcion("");
      await fetchCaja();
    } catch (e) {
      setError(e?.response?.data?.message || e?.message || "Error al registrar movimiento.");
    } finally {
      setMovProcessing(false);
    }
  };

  const cajaSeleccionada = useMemo(
    () => cajas.find((c) => Number(c.id_caja) === Number(idCaja)) || null,
    [cajas, idCaja]
  );

  const cierreAgrupado = useMemo(() => {
    const movs = cierreReporte?.movimientos || [];
    const byCat = new Map();
    for (const m of movs) {
      const cat = m.categoria || "OTROS";
      if (!byCat.has(cat)) byCat.set(cat, []);
      byCat.get(cat).push(m);
    }

    const cats = Array.from(byCat.keys()).sort();
    const resumen = cats.map((cat) => {
      const rows = byCat.get(cat);
      const ingresos = rows.reduce((acc, r) => acc + (r.tipo === "INGRESO" ? Number(r.monto || 0) : 0), 0);
      const egresos = rows.reduce((acc, r) => acc + (r.tipo === "EGRESO" ? Number(r.monto || 0) : 0), 0);
      return { cat, rows, ingresos: +ingresos.toFixed(2), egresos: +egresos.toFixed(2) };
    });

    const ingresosTotal = resumen.reduce((acc, r) => acc + r.ingresos, 0);
    const egresosTotal = resumen.reduce((acc, r) => acc + r.egresos, 0);
    return { resumen, ingresosTotal: +ingresosTotal.toFixed(2), egresosTotal: +egresosTotal.toFixed(2) };
  }, [cierreReporte]);

  return (
    <PageContainer title="Caja">
      <Stack spacing={2}>
        {error && <Alert severity="error">{error}</Alert>}

        <Card>
          <CardContent>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems={{ sm: "center" }}>
              <TextField
                label="Caja"
                select
                value={idCaja}
                onChange={(e) => setIdCaja(Number(e.target.value))}
                sx={{ minWidth: { xs: "100%", sm: 280 } }}
              >
                {(cajas || []).map((c) => (
                  <MenuItem key={c.id_caja} value={c.id_caja}>
                    {c.nombre} {c.ubicacion ? `- ${c.ubicacion}` : ""} (#{c.id_caja})
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                label="Filtrar movimientos"
                select
                value={categoriaFiltro}
                onChange={(e) => setCategoriaFiltro(e.target.value)}
                sx={{ minWidth: { xs: "100%", sm: 220 } }}
              >
                <MenuItem value="ALL">Todos</MenuItem>
                {categoriasDisponibles.map((cat) => (
                  <MenuItem key={cat} value={cat}>
                    {cat}
                  </MenuItem>
                ))}
              </TextField>
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Caja seleccionada: <strong>{cajaSeleccionada?.nombre || `#${idCaja}`}</strong>
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  La apertura/cierre y movimientos se controlan por caja.
                </Typography>
                {categoriaFiltro !== "ALL" && (
                  <Typography variant="caption" color="text.secondary" display="block">
                    Ingresos: <strong>{formatMoney(totalesFiltrados.ingresos)}</strong> · Egresos:{" "}
                    <strong>{formatMoney(totalesFiltrados.egresos)}</strong>
                  </Typography>
                )}
              </Box>
              <Box sx={{ flexGrow: 1 }} />
              <Button variant="outlined" onClick={fetchCaja} disabled={loading || processing}>
                Actualizar
              </Button>
            </Stack>
          </CardContent>
        </Card>

        {loading ? (
          <Typography>Cargando...</Typography>
        ) : sesion ? (
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" fontWeight={700}>
                    Sesión abierta
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    ID sesión: {sesion.id_caja_sesion}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Apertura: {String(sesion.fecha_apertura || "")}
                  </Typography>

                  <Divider sx={{ my: 2 }} />

                  <Stack spacing={1}>
                    <Typography>Inicial: <strong>{formatMoney(totales.inicial)}</strong></Typography>
                    <Typography>Ingresos: <strong>{formatMoney(totales.ingresos)}</strong></Typography>
                    <Typography>Egresos: <strong>{formatMoney(totales.egresos)}</strong></Typography>
                    <Typography variant="h6">
                      Sistema: <strong>{formatMoney(totales.sistema)}</strong>
                    </Typography>
                  </Stack>

                  <Divider sx={{ my: 2 }} />

                  <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                    <TextField
                      label="Monto contado (cierre)"
                      value={montoCierre}
                      onChange={(e) => setMontoCierre(e.target.value)}
                      fullWidth
                      type="number"
                      inputProps={{ step: "0.01", min: 0 }}
                    />
                    <Button
                      variant="contained"
                      color="error"
                      onClick={handleCerrar}
                      disabled={processing}
                      sx={{ minWidth: 160 }}
                    >
                      Cerrar caja
                    </Button>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" fontWeight={700}>
                    Movimientos
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Mostrando {movimientosFiltrados.length} movimientos.
                  </Typography>

                  <Divider sx={{ my: 2 }} />

                  <Typography variant="subtitle1" fontWeight={700} gutterBottom>
                    Otros ingresos / egresos
                  </Typography>

                  <Stack spacing={1.5}>
                    <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
                      <TextField
                        select
                        label="Tipo"
                        value={movTipo}
                        onChange={(e) => setMovTipo(e.target.value)}
                        fullWidth
                      >
                        <MenuItem value="INGRESO">INGRESO</MenuItem>
                        <MenuItem value="EGRESO">EGRESO</MenuItem>
                      </TextField>
                      <TextField
                        label="Categoria"
                        value={movCategoria}
                        onChange={(e) => setMovCategoria(e.target.value)}
                        fullWidth
                        placeholder="AJUSTE, BANCO, GASTOS..."
                      />
                    </Stack>

                    <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
                      <TextField
                        select
                        label="Método pago"
                        value={movMetodoPago}
                        onChange={(e) => setMovMetodoPago(e.target.value)}
                        fullWidth
                      >
                        <MenuItem value="EFECTIVO">EFECTIVO</MenuItem>
                        <MenuItem value="TRANSFERENCIA">TRANSFERENCIA</MenuItem>
                        <MenuItem value="TARJETA">TARJETA</MenuItem>
                        <MenuItem value="OTRO">OTRO</MenuItem>
                      </TextField>
                      <TextField
                        label="Monto"
                        value={movMonto}
                        onChange={(e) => setMovMonto(e.target.value)}
                        fullWidth
                        type="number"
                        inputProps={{ step: "0.01", min: 0 }}
                      />
                    </Stack>

                    <TextField
                      label="Descripción"
                      value={movDescripcion}
                      onChange={(e) => setMovDescripcion(e.target.value)}
                      fullWidth
                      multiline
                      minRows={2}
                    />

                    <Button
                      variant="contained"
                      onClick={handleRegistrarMovimiento}
                      disabled={movProcessing}
                    >
                      Registrar movimiento
                    </Button>
                  </Stack>

                  <Divider sx={{ my: 2 }} />

                  <Box sx={{ maxHeight: 420, overflow: "auto" }}>
                    {movimientosFiltrados.map((m) => (
                      <Box key={m.id_caja_movimiento} sx={{ py: 1 }}>
                        <Stack direction="row" justifyContent="space-between" spacing={2}>
                          <Typography variant="body2">
                            <strong>{m.tipo}</strong> · {m.categoria} · {m.metodo_pago}
                          </Typography>
                          <Typography variant="body2" fontWeight={700}>
                            {formatMoney(m.monto)}
                          </Typography>
                        </Stack>
                        <Typography variant="caption" color="text.secondary">
                          {String(m.fecha || "")} {m.descripcion ? `· ${m.descripcion}` : ""}
                        </Typography>
                        <Divider sx={{ mt: 1 }} />
                      </Box>
                    ))}
                    {movimientosFiltrados.length === 0 && (
                      <Typography variant="body2" color="text.secondary">
                        Sin movimientos aún.
                      </Typography>
                    )}
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        ) : (
          <Card>
            <CardContent>
              <Typography variant="h6" fontWeight={700}>
                No hay caja abierta
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Abre caja para poder registrar ventas/abonos en efectivo.
              </Typography>

              <Divider sx={{ my: 2 }} />

              <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                <TextField
                  label="Monto inicial"
                  value={montoInicial}
                  onChange={(e) => setMontoInicial(e.target.value)}
                  fullWidth
                  type="number"
                  inputProps={{ step: "0.01", min: 0 }}
                />
                <Button
                  variant="contained"
                  onClick={handleAbrir}
                  disabled={processing}
                  sx={{ minWidth: 160 }}
                >
                  Abrir caja
                </Button>
              </Stack>
            </CardContent>
          </Card>
        )}
      </Stack>

      <Dialog
        open={cierreModalOpen}
        onClose={() => setCierreModalOpen(false)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>Evidencia de cierre de caja</DialogTitle>
        <DialogContent dividers>
          {!cierreReporte ? (
            <Typography variant="body2">Sin datos.</Typography>
          ) : (
            <Stack spacing={2}>
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Caja: <strong>{cierreReporte?.caja?.nombre || `#${cierreReporte.id_caja}`}</strong>
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Sesión: <strong>{cierreReporte?.sesion?.id_caja_sesion}</strong> · Apertura:{" "}
                  <strong>{String(cierreReporte?.sesion?.fecha_apertura || "")}</strong>
                </Typography>
              </Box>

              <Divider />

              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography>Inicial: <strong>{formatMoney((cierreReporte.totales_caja || cierreReporte.totales)?.inicial)}</strong></Typography>
                  <Typography>Ingresos: <strong>{formatMoney(cierreAgrupado.ingresosTotal)}</strong></Typography>
                  <Typography>Egresos: <strong>{formatMoney(cierreAgrupado.egresosTotal)}</strong></Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography>
                    Sistema:{" "}
                    <strong>{formatMoney(cierreReporte.result?.monto_sistema ?? (cierreReporte.totales_caja || cierreReporte.totales)?.sistema)}</strong>
                  </Typography>
                  <Typography>
                    Contado: <strong>{formatMoney(cierreReporte.monto_cierre_reportado)}</strong>
                  </Typography>
                  <Typography>
                    Diferencia: <strong>{formatMoney(cierreReporte.result?.diferencia ?? 0)}</strong>
                  </Typography>
                </Grid>
              </Grid>

              <Divider />

              <Typography variant="h6">Movimientos (agrupados)</Typography>
              {cierreReporte.resumen_rafaga && <Alert severity="info">
                <Typography fontWeight={700}>Zona de Ráfaga · todos los métodos de pago</Typography>
                <Typography>Cobrado: {formatMoney(cierreReporte.resumen_rafaga.total)} · Para boletas del negocio: {formatMoney(cierreReporte.resumen_rafaga.costo_boletas)} · Ganancia: {formatMoney(cierreReporte.resumen_rafaga.ganancia)}</Typography>
                <Typography variant="body2">Boletas del negocio: {cierreReporte.resumen_rafaga.del_negocio} · Traídas por clientes: {cierreReporte.resumen_rafaga.del_cliente}. La reserva de boletas es parte del cobro; no registra un egreso adicional.</Typography>
              </Alert>}
              {cierreAgrupado.resumen.map(({ cat, rows, ingresos, egresos }) => (
                <Card key={cat} variant="outlined">
                  <CardContent>
                    <Stack direction={{ xs: "column", sm: "row" }} spacing={1} alignItems={{ sm: "center" }}>
                      <Typography variant="subtitle1" fontWeight={700}>
                        {cat}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Ingresos: <strong>{formatMoney(ingresos)}</strong> · Egresos:{" "}
                        <strong>{formatMoney(egresos)}</strong> · Movs: <strong>{rows.length}</strong>
                      </Typography>
                    </Stack>
                    <Divider sx={{ my: 1.5 }} />
                    <Stack spacing={1}>
                      {rows.map((m) => (
                        <Box key={m.id_caja_movimiento}>
                          <Stack direction="row" justifyContent="space-between" spacing={2}>
                            <Typography variant="body2">
                              <strong>{m.tipo}</strong> · {m.metodo_pago} {m.descripcion ? `· ${m.descripcion}` : ""}
                            </Typography>
                            <Typography variant="body2" fontWeight={700}>
                              {formatMoney(m.monto)}
                            </Typography>
                          </Stack>
                          <Typography variant="caption" color="text.secondary">
                            {String(m.fecha || "")}
                          </Typography>
                          <Divider sx={{ mt: 1 }} />
                        </Box>
                      ))}
                    </Stack>
                  </CardContent>
                </Card>
              ))}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            variant="outlined"
            onClick={() => {
              if (!cierreReporte) return;
              const blob = new Blob([JSON.stringify(cierreReporte, null, 2)], { type: "application/json" });
              const url = URL.createObjectURL(blob);
              const a = document.createElement("a");
              a.href = url;
              a.download = `cierre_caja_${cierreReporte?.sesion?.id_caja_sesion || "sesion"}.json`;
              a.click();
              URL.revokeObjectURL(url);
            }}
            disabled={!cierreReporte}
          >
            Descargar evidencia (JSON)
          </Button>
          <Button
            variant="contained"
            onClick={() => generarPDFCierre(cierreReporte)}
            disabled={!cierreReporte}
          >
            Descargar PDF
          </Button>
          <Button onClick={() => setCierreModalOpen(false)}>Cerrar</Button>
        </DialogActions>
      </Dialog>
    </PageContainer>
  );
};

export default Caja;
